/**
 * Управление одним прогоном набора.
 *
 * Слушатель клавиатуры вешается на документ: пользователь не должен искать
 * поле ввода, чтобы начать печатать. Состояние обновляется точечно, чтобы
 * перерисовывался текст, а не весь экран (NFR-P-03).
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { LayoutId, OsPlatform, SessionId, TypingMetrics } from '@type2x/shared-contracts';
import { EMPTY_METRICS } from '@type2x/shared-contracts';
import { TypingSession, type SessionState } from '@type2x/typing-engine';
import {
  IntegrityRecorder,
  attachIntegrityListeners,
  isSyntheticEvent,
  normalizeKeyEvent,
} from '@type2x/adapter-web';
import type { IntegritySignals } from '@type2x/adapter-web';

import type { Runtime } from '../runtime.js';

/** Параметры прогона. */
export interface TypingRunOptions {
  readonly sessionId: SessionId;
  readonly layoutId: LayoutId;
  /** Операционная система: от неё зависит расположение части клавиш. */
  readonly platform: OsPlatform;
  readonly targetText: string;
  readonly allowCorrections?: boolean;
  /** Собирать ли сигналы честности: нужно только для контрольного теста. */
  readonly trackIntegrity?: boolean;
  /** Вызывается на каждый промах: экран решает, подавать ли сигнал. */
  readonly onError?: () => void;
  /** Вызывается один раз при завершении текста. */
  readonly onFinished: (session: TypingSession, signals: IntegritySignals) => void;
}

/** Наблюдаемое состояние прогона. */
export interface TypingRunState {
  readonly position: number;
  readonly typedText: string;
  readonly metrics: TypingMetrics;
  readonly finished: boolean;
  readonly expectedCharacter: string;
  readonly session: TypingSession;
}

/** Что событие клавиатуры сделало с сессией. */
interface KeyResult {
  /** Состояние изменилось и его нужно синхронизировать. */
  readonly changed: boolean;
  /** Нажата не та клавиша. Возврат назад промахом не считается. */
  readonly missed: boolean;
}

const IGNORED: KeyResult = { changed: false, missed: false };

/** Применяет событие клавиатуры к сессии. */
function applyEvent(event: KeyboardEvent, session: TypingSession, monotonicMs: number): KeyResult {
  const action = normalizeKeyEvent(event);
  if (action.kind === 'ignore') return IGNORED;

  event.preventDefault();
  if (action.kind === 'backspace') {
    session.backspace();
    return { changed: true, missed: false };
  }

  // Дописанная сессия нажатия не засчитывает и отвечает на них «неверно».
  // Проверка делается до применения: иначе неверная последняя буква — та,
  // которая сессию и завершает, — осталась бы без сигнала.
  const wasFinished = session.finished;
  const outcome = session.applyKeystroke({
    character: action.character,
    monotonicMs,
    modifiers: action.modifiers,
  });

  return { changed: true, missed: !wasFinished && !outcome.correct };
}

/** Снимок состояния набора для отрисовки. */
interface Snapshot {
  readonly position: number;
  readonly typedText: string;
  readonly metrics: TypingMetrics;
  readonly finished: boolean;
}

/** Переносит состояние сессии в снимок для отрисовки. */
function snapshotOf(state: SessionState): Snapshot {
  return {
    position: state.position,
    typedText: state.typedText,
    metrics: state.metrics,
    finished: state.finished,
  };
}

const EMPTY_SNAPSHOT: Snapshot = {
  position: 0,
  typedText: '',
  metrics: EMPTY_METRICS,
  finished: false,
};

/**
 * Сессия набора для текущего прогона.
 *
 * Пересоздаётся при смене любого параметра: сессия, пережившая смену текста
 * или раскладки, считала бы нажатия по чужому упражнению.
 */
function useSession(options: TypingRunOptions): TypingSession {
  const { sessionId, layoutId, platform, targetText, allowCorrections } = options;

  return useMemo(
    () =>
      new TypingSession({
        sessionId,
        layoutId,
        platform,
        targetText,
        allowCorrections: allowCorrections ?? true,
      }),
    [sessionId, layoutId, platform, targetText, allowCorrections],
  );
}

export function useTypingRun(runtime: Runtime, options: TypingRunOptions): TypingRunState {
  const session = useSession(options);

  // Одно состояние вместо четырёх: снимок обновляется целиком после нажатия,
  // поэтому раздельные setState вызывали бы лишние проходы рендера.
  const [snapshot, setSnapshot] = useState<Snapshot>(EMPTY_SNAPSHOT);
  const finishedRef = useRef(false);
  const recorderRef = useRef(new IntegrityRecorder());

  // Смена сессии обнуляет снимок и флаг завершения: без этого компонент,
  // переиспользованный React для следующего упражнения, считал бы его
  // уже завершённым.
  useEffect(() => {
    finishedRef.current = false;
    recorderRef.current = new IntegrityRecorder();
    setSnapshot(EMPTY_SNAPSHOT);
  }, [session]);

  // Наблюдение за вставкой и потерей фокуса включается только там, где от
  // него есть польза: в обычной тренировке эти события ничего не решают.
  useEffect(() => {
    if (options.trackIntegrity !== true) return undefined;
    return attachIntegrityListeners(recorderRef.current);
  }, [options.trackIntegrity, session]);

  const sync = useCallback(() => {
    const state = session.state;
    setSnapshot(snapshotOf(state));

    if (state.finished && !finishedRef.current) {
      finishedRef.current = true;
      options.onFinished(session, recorderRef.current.collect(session.keystrokeEvents));
    }
  }, [session, options]);

  const { onError } = options;

  const handleKeyDown = useCallback(
    (event: KeyboardEvent): void => {
      if (isSyntheticEvent(event)) recorderRef.current.recordUntrustedEvent();

      const result = applyEvent(event, session, runtime.clock.monotonicMs());
      // Сигнал подаётся до перерисовки: звук об ошибке, пришедший после того,
      // как человек напечатал следующую букву, указывает не на ту клавишу.
      if (result.missed) onError?.();
      if (result.changed) sync();
    },
    [runtime, session, sync, onError],
  );

  useEffect(() => {
    globalThis.document.addEventListener('keydown', handleKeyDown);
    return () => globalThis.document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  return { ...snapshot, expectedCharacter: session.expectedCharacter, session };
}
